import type { ComponentProps, ReactNode } from 'react';

import { tv } from '../../lib/tv';
import { Separator } from '../ui/separator';

export type DividerProps = Omit<ComponentProps<typeof Separator>, 'children'> & {
  /** Figma "Orientation": Horizontal fills the width; Vertical is 24px tall (override with h-*). */
  orientation?: 'horizontal' | 'vertical';
  /** Figma "Tone": Subtle inside cards and menus; Default between page regions. */
  tone?: 'subtle' | 'default';
  /** Figma "Label text" (Label=Yes, horizontal only), e.g. "or" between SSO and email sign-in. */
  label?: ReactNode;
};

const divider = tv({
  slots: {
    root: 'flex w-full items-center gap-3',
    line: '',
    label: 'text-caption text-text-tertiary shrink-0',
  },
  variants: {
    tone: {
      subtle: { line: 'bg-border-subtle' },
      default: { line: 'bg-border-default' },
    },
    labelled: { true: { line: 'flex-1' } },
  },
});

/**
 * Separates groups of content (Figma: Components ↳ Controls ↳ Divider).
 * Prefer spacing when groups are already distinct. Decorative by default
 * (hidden from assistive tech); pass `decorative={false}` for a semantic
 * separator.
 */
export function Divider({
  orientation = 'horizontal',
  tone = 'default',
  label,
  className,
  ...props
}: DividerProps) {
  const labelled = label !== undefined && orientation === 'horizontal';
  const styles = divider({ tone, labelled });
  if (!labelled)
    return (
      <Separator
        orientation={orientation}
        className={styles.line({ className })}
        {...props}
      />
    );
  return (
    <div data-slot="divider" className={styles.root({ className })}>
      <Separator className={styles.line()} {...props} />
      <span className={styles.label()}>{label}</span>
      <Separator className={styles.line()} {...props} />
    </div>
  );
}
